import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Pencil, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Calendar } from "../ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../ui/popover";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../ui/alert-dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { confirmDialog } from "../ui/confirm-dialog";

const SpareParts = ({ axiosInstance }) => {
    // State for Stock Spare Part Management
    const [spareParts, setSpareParts] = useState([]);
    const [isAddSparePartOpen, setIsAddSparePartOpen] = useState(false);
    const [isAddSparePartConfirmOpen, setIsAddSparePartConfirmOpen] = useState(false);
    const [isEditSparePartOpen, setIsEditSparePartOpen] = useState(false);
    const [selectedSparePart, setSelectedSparePart] = useState(null);
    const [newSpareParts, setNewSpareParts] = useState([{
        brand: '',
        model: '',
        itemDescription: '',
        itemCode: '',
        quantity: ''
    }]);
    const [siNumber, setSiNumber] = useState("");
    const [drNumber, setDrNumber] = useState("");
    const [supplier, setSupplier] = useState("");
    const [deliveryDate, setDeliveryDate] = useState(null);

    // State for Brand Management
    const [brands, setBrands] = useState([]);
    const [isAddBrandOpen, setIsAddBrandOpen] = useState(false);
    const [newBrandName, setNewBrandName] = useState("");

    // State for Spare Part Details Modal (Aggregation)
    const [isSparePartDetailsOpen, setIsSparePartDetailsOpen] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);
    const [modelSpareParts, setModelSpareParts] = useState([]);
    
    // User info
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdmin = user.email === 'vinsimon23@gmail.com';

    // Filtering & Sorting State
    const [brandFilter, setBrandFilter] = useState("");
    const [searchFilter, setSearchFilter] = useState("");
    const [sortOption, setSortOption] = useState("alphabetical");
    const [mainWarehouseId, setMainWarehouseId] = useState(null);

    // Fetch data on component mount
    useEffect(() => {
        if (axiosInstance) {
            fetchWarehouses();
            fetchSpareParts();
            fetchBrands();
        }
    }, [axiosInstance]);

    const fetchWarehouses = async () => {
        try {
            const response = await axiosInstance.get('/inventory/warehouses');
            const warehouses = response.data;
            const main = warehouses.find(w => w.name === "Main Warehouse") || warehouses[0];
            if (main) {
                setMainWarehouseId(main.id);
            }
        } catch (error) {
            console.error("Error fetching warehouses:", error);
        }
    };

    const fetchSpareParts = async () => {
        try {
            const response = await axiosInstance.get('/stock/spare-parts');
            setSpareParts(response.data);
        } catch (error) {
            console.error("Error fetching spare parts:", error);
            toast.error("Failed to fetch spare parts");
        }
    };

    const fetchBrands = async () => {
        try {
            const response = await axiosInstance.get('/stock/spare-parts/brands');
            setBrands(response.data);
        } catch (error) {
            console.error("Error fetching brands:", error);
            toast.error("Failed to fetch brands");
        }
    };

    const handleSparePartInputChange = (index, e) => {
        const { name, value } = e.target;
        const updatedSpareParts = [...newSpareParts];
        updatedSpareParts[index] = {
            ...updatedSpareParts[index],
            [name]: value
        };
        setNewSpareParts(updatedSpareParts);
    };

    const handleAddSparePartRow = () => {
        setNewSpareParts([...newSpareParts, {
            brand: '',
            model: '',
            itemDescription: '',
            itemCode: '',
            quantity: ''
        }]);
    };

    const handleRemoveSparePartRow = (index) => {
        if (newSpareParts.length > 1) {
            const updatedSpareParts = newSpareParts.filter((_, i) => i !== index);
            setNewSpareParts(updatedSpareParts);
        }
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setSelectedSparePart(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSaveSparePart = async () => {
        // Basic validation
        for (const sparePart of newSpareParts) {
            if (!sparePart.brand || !sparePart.itemCode || !sparePart.quantity) {
                toast.error("Please fill in required fields for all rows");
                return;
            }
        }

        try {
            const toCreate = newSpareParts.map(sparePart => ({
                ...sparePart,
                model: sparePart.model || '-', // Default model to '-' if empty
                quantity: parseInt(sparePart.quantity) || 0,
                si_number: siNumber,
                dr_number: drNumber,
                supplier: supplier,
                delivery_date: deliveryDate
            }));

            if (toCreate.length > 0) {
                await axiosInstance.post('/stock/spare-parts', toCreate);
            }

            toast.success("Spare parts processed successfully");
            setIsAddSparePartOpen(false);
            setNewSpareParts([{
                brand: '',
                model: '',
                itemDescription: '',
                itemCode: '',
                quantity: ''
            }]);
            setSiNumber("");
            setDrNumber("");
            setSupplier("");
            setDeliveryDate(null);
            fetchSpareParts(); // Refresh list
        } catch (error) {
            console.error("Error saving spare parts:", error);
            toast.error("Failed to save spare parts");
        }
    };

    const handleRowClick = (aggregatedSparePart) => {
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        // Use filteredSpareParts instead of raw spareParts to ensure consistency
        const relatedSpareParts = filteredSpareParts.filter(s => 
            normalize(s.model) === normalize(aggregatedSparePart.model) && 
            normalize(s.brand) === normalize(aggregatedSparePart.brand) &&
            normalize(s.itemDescription) === normalize(aggregatedSparePart.itemDescription) &&
            normalize(s.itemCode) === normalize(aggregatedSparePart.itemCode)
        );
        setSelectedModel(aggregatedSparePart);
        setModelSpareParts(relatedSpareParts);
        setIsSparePartDetailsOpen(true);
    };

    const handleEditSparePart = (sparePart) => {
        setSelectedSparePart(sparePart);
        setIsEditSparePartOpen(true);
    };

    const handleDeleteSparePart = async (sparePartId) => {
        const confirmed = await confirmDialog({
            title: "Delete Spare Part",
            description: "Are you sure you want to delete this spare part?",
            variant: "destructive"
        });
        if (!confirmed) return;

        try {
            await axiosInstance.delete(`/stock/spare-parts/${sparePartId}`);
            toast.success("Spare part deleted successfully");
            
            // Refresh main list
            await fetchSpareParts();
            
            // Update modal list
            setModelSpareParts(prev => prev.filter(s => s.id !== sparePartId));
            
            // If no spare parts left for this model, close modal
            if (modelSpareParts.length <= 1) {
                setIsSparePartDetailsOpen(false);
            }
        } catch (error) {
            console.error("Error deleting spare part:", error);
            toast.error("Failed to delete spare part");
        }
    };

    const handleUpdateSparePart = async () => {
        if (!selectedSparePart) return;
        
        try {
            const payload = {
                brand: selectedSparePart.brand,
                model: selectedSparePart.model,
                itemDescription: selectedSparePart.itemDescription,
                itemCode: selectedSparePart.itemCode,
                quantity: parseInt(selectedSparePart.quantity) || 0
            };

            await axiosInstance.put(`/stock/spare-parts/${selectedSparePart.id}`, payload);
            toast.success("Spare part updated successfully");
            setIsEditSparePartOpen(false);
            setSelectedSparePart(null);
            
            // Refresh main list
            await fetchSpareParts();
            
            // Update local state for details modal if it's open
            if (isSparePartDetailsOpen && selectedModel) {
                const response = await axiosInstance.get('/stock/spare-parts');
                const allSpareParts = response.data;
                const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
                const updatedModelSpareParts = allSpareParts.filter(s => 
                    normalize(s.model) === normalize(selectedModel.model) && 
                    normalize(s.brand) === normalize(selectedModel.brand) &&
                    normalize(s.itemDescription) === normalize(selectedModel.itemDescription) &&
                    normalize(s.itemCode) === normalize(selectedModel.itemCode) &&
                    (!mainWarehouseId || !s.warehouse_id || s.warehouse_id === mainWarehouseId)
                );
                setModelSpareParts(updatedModelSpareParts);
            }
        } catch (error) {
            console.error("Error updating spare part:", error);
            toast.error("Failed to update spare part");
        }
    };

    const handleSaveBrand = async () => {
        if (!newBrandName.trim()) {
            toast.error("Brand name cannot be empty");
            return;
        }

        try {
            await axiosInstance.post('/stock/spare-parts/brands', { name: newBrandName.trim() });
            toast.success("Brand added successfully");
            setNewBrandName("");
            fetchBrands(); // Refresh list
        } catch (error) {
            console.error("Error saving brand:", error);
            if (error.response && error.response.status === 400) {
                toast.error("Brand already exists");
            } else {
                toast.error("Failed to save brand");
            }
        }
    };

    const handleDeleteBrand = async (brandId) => {
        try {
            await axiosInstance.delete(`/stock/spare-parts/brands/${brandId}`);
            toast.success("Brand deleted successfully");
            fetchBrands(); // Refresh list
        } catch (error) {
            console.error("Error deleting brand:", error);
            toast.error("Failed to delete brand");
        }
    };

    // Filter and Sort Spare Parts
    const filteredSpareParts = spareParts.filter(sparePart => {
        const matchesBrand = brandFilter && brandFilter !== "all" ? sparePart.brand === brandFilter : true;
        const searchLower = searchFilter.toLowerCase();
        const matchesSearch = !searchFilter || 
            (sparePart.itemCode?.toLowerCase().includes(searchLower) || 
             sparePart.itemDescription?.toLowerCase().includes(searchLower));
        
        // Filter by last 24 hours (1 day recent added items)
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        const matchesDate = sparePart.createdAt && new Date(sparePart.createdAt) >= oneDayAgo;

        // Filter by Main Warehouse (exclude transferred items)
        const matchesWarehouse = !mainWarehouseId || !sparePart.warehouse_id || sparePart.warehouse_id === mainWarehouseId;

        return matchesBrand && matchesSearch && matchesDate && matchesWarehouse;
    });

    // Aggregate Spare Parts by Brand + Model + Description + ItemCode
    const aggregatedSpareParts = Object.values(filteredSpareParts.reduce((acc, sparePart) => {
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const key = `${normalize(sparePart.brand)}-${normalize(sparePart.model)}-${normalize(sparePart.itemDescription)}-${normalize(sparePart.itemCode)}`;
        
        if (!acc[key]) {
            acc[key] = {
                ...sparePart,
                totalQuantity: 0,
                count: 0
            };
        } else {
            if (acc[key].si_number !== 'Multiple' && acc[key].si_number !== sparePart.si_number) {
                acc[key].si_number = 'Multiple';
            }
            if (acc[key].dr_number !== 'Multiple' && acc[key].dr_number !== sparePart.dr_number) {
                acc[key].dr_number = 'Multiple';
            }
        }
        acc[key].totalQuantity += (sparePart.quantity || 0);
        acc[key].count += 1;
        return acc;
    }, {}));

    const sortedAggregatedSpareParts = aggregatedSpareParts.sort((a, b) => {
        switch (sortOption) {
            case "largest_qty":
                return b.totalQuantity - a.totalQuantity;
            case "lowest_qty":
                return a.totalQuantity - b.totalQuantity;
            case "alphabetical":
            default:
                return a.brand.localeCompare(b.brand) || a.itemCode.localeCompare(b.itemCode);
        }
    });

    return (
        <div className="p-6 space-y-6 h-full flex flex-col">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Spare Parts Inventory</h2>
            </div>

            {/* Split Layout: 30% Left, 70% Right */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-[30%_70%] gap-6">
                
                {/* Left Side: Controls */}
                <div className="bg-white p-4 rounded-lg shadow flex flex-col h-full">
                    {/* Top Left Buttons */}
                    <div className="flex justify-start mb-4 gap-2">
                        <Button onClick={() => setIsAddSparePartOpen(true)} className="flex items-center gap-2">
                            <Plus className="h-4 w-4" />
                            Spare Parts
                        </Button>
                        <Button onClick={() => setIsAddBrandOpen(true)} variant="outline" className="flex items-center gap-2">
                            <Plus className="h-4 w-4" />
                            Brand
                        </Button>
                    </div>

                    {/* Filtering Section */}
                    <div className="flex flex-col gap-4">
                        <div className="space-y-2">
                            <Label>Brand</Label>
                            <Select value={brandFilter} onValueChange={setBrandFilter}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select Brand" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Brands</SelectItem>
                                    {brands.map(brand => (
                                        <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Search</Label>
                            <div className="flex gap-2">
                                <Input
                                    placeholder="Search Item Code or Description..."
                                    value={searchFilter}
                                    onChange={(e) => setSearchFilter(e.target.value)}
                                    className="flex-1"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side: Stock List Table */}
                <div className="bg-white p-4 rounded-lg shadow flex flex-col h-full">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">Recent Activity</h3>
                        <div className="w-[200px]">
                            <Select value={sortOption} onValueChange={setSortOption}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Sort By" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="alphabetical">Alphabetical (A-Z)</SelectItem>
                                    <SelectItem value="largest_qty">Largest Quantity</SelectItem>
                                    <SelectItem value="lowest_qty">Lowest Quantity</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    
                    <div className="border rounded-md flex-1 overflow-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>SI No.</TableHead>
                                    <TableHead>DR No.</TableHead>
                                    <TableHead className="font-bold">Brand</TableHead>
                                    <TableHead>Item Description</TableHead>
                                    <TableHead>Part Number</TableHead>
                                    <TableHead>Total Quantity</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedAggregatedSpareParts.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} className="text-center py-4 text-gray-500">
                                            No spare parts found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sortedAggregatedSpareParts.map((sparePart, index) => (
                                        <TableRow 
                                            key={`${sparePart.brand}-${sparePart.itemCode}-${index}`} 
                                            className="hover:bg-green-100 cursor-pointer transition-colors"
                                            onClick={() => handleRowClick(sparePart)}
                                        >
                                            <TableCell>{sparePart.si_number === 'Multiple' ? 'Multiple' : (sparePart.si_number || '-')}</TableCell>
                                            <TableCell>{sparePart.dr_number === 'Multiple' ? 'Multiple' : (sparePart.dr_number || '-')}</TableCell>
                                            <TableCell className="font-bold">{sparePart.brand}</TableCell>
                                            <TableCell>{sparePart.itemDescription}</TableCell>
                                            <TableCell>{sparePart.itemCode}</TableCell>
                                            <TableCell>{sparePart.totalQuantity}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>

            {/* Spare Part Details Modal */}
            <Dialog open={isSparePartDetailsOpen} onOpenChange={setIsSparePartDetailsOpen}>
                <DialogContent className="max-w-[85vw] max-h-[80vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedModel ? `${selectedModel.brand} ${selectedModel.model}` : 'Spare Part Details'}
                        </DialogTitle>
                        <DialogDescription>
                            Entries: {modelSpareParts.length}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-auto mt-4">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    {isSuperAdmin && <TableHead>Added By</TableHead>}
                                    {isSuperAdmin && <TableHead>Date Created</TableHead>}
                                    <TableHead>Brand</TableHead>
                                    <TableHead>Item Description</TableHead>
                                    <TableHead>Part Number</TableHead>
                                    <TableHead>SI No.</TableHead>
                                    <TableHead>DR No.</TableHead>
                                    <TableHead>Supplier</TableHead>
                                    <TableHead>Delivery Date</TableHead>
                                    <TableHead>Quantity</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {modelSpareParts.map((sparePart) => (
                                    <TableRow key={sparePart.id}>
                                        {isSuperAdmin && <TableCell>{sparePart.addedBy || '-'}</TableCell>}
                                        {isSuperAdmin && <TableCell>{new Date(sparePart.createdAt).toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' })}</TableCell>}
                                        <TableCell>{sparePart.brand}</TableCell>
                                        <TableCell>{sparePart.itemDescription}</TableCell>
                                        <TableCell>{sparePart.itemCode}</TableCell>
                                        <TableCell>{sparePart.si_number || '-'}</TableCell>
                                        <TableCell>{sparePart.dr_number || '-'}</TableCell>
                                        <TableCell>{sparePart.supplier || '-'}</TableCell>
                                        <TableCell>{sparePart.delivery_date ? new Date(sparePart.delivery_date).toLocaleDateString() : '-'}</TableCell>
                                        <TableCell>{sparePart.quantity}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => handleEditSparePart(sparePart)}
                                                >
                                                    <Pencil className="h-4 w-4 text-blue-500" />
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => handleDeleteSparePart(sparePart.id)}
                                                >
                                                    <Trash2 className="h-4 w-4 text-red-500" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsSparePartDetailsOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Spare Part Modal */}
            <Dialog open={isAddSparePartOpen} onOpenChange={setIsAddSparePartOpen}>
                <DialogContent className="max-w-[90vw] w-full max-h-[90vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Add New Spare Parts</DialogTitle>
                        <DialogDescription>
                            Enter the details of the new spare part stock.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="grid grid-cols-3 gap-4 p-4 border-b">
                        <div className="grid gap-2">
                            <Label htmlFor="siNumber">SI Number</Label>
                            <Input
                                id="siNumber"
                                value={siNumber}
                                onChange={(e) => setSiNumber(e.target.value)}
                                placeholder="Enter SI Number"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="drNumber">DR Number</Label>
                            <Input
                                id="drNumber"
                                value={drNumber}
                                onChange={(e) => setDrNumber(e.target.value)}
                                placeholder="Enter DR Number"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="supplier">Supplier</Label>
                            <Input
                                id="supplier"
                                value={supplier}
                                onChange={(e) => setSupplier(e.target.value)}
                                placeholder="Enter Supplier"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label>Date of Delivery</Label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant={"outline"}
                                        className={cn(
                                            "w-full justify-start text-left font-normal",
                                            !deliveryDate && "text-muted-foreground"
                                        )}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {deliveryDate ? format(deliveryDate, "PPP") : <span>Pick a date</span>}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0">
                                    <Calendar
                                        mode="single"
                                        selected={deliveryDate}
                                        onSelect={setDeliveryDate}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto py-4">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Brand</TableHead>
                                    <TableHead>Description</TableHead>
                                    <TableHead>Part Number</TableHead>
                                    <TableHead>Quantity</TableHead>
                                    <TableHead className="w-[50px]"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {newSpareParts.map((sparePart, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Select 
                                                value={sparePart.brand} 
                                                onValueChange={(value) => handleSparePartInputChange(index, { target: { name: 'brand', value } })}
                                            >
                                                <SelectTrigger className="w-[150px]">
                                                    <SelectValue placeholder="Brand" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {brands.map(brand => (
                                                        <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                name="itemDescription"
                                                value={sparePart.itemDescription}
                                                onChange={(e) => handleSparePartInputChange(index, e)}
                                                placeholder="Description"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                name="itemCode"
                                                value={sparePart.itemCode}
                                                onChange={(e) => handleSparePartInputChange(index, e)}
                                                placeholder="Part Number"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                name="quantity"
                                                type="number"
                                                value={sparePart.quantity}
                                                onChange={(e) => handleSparePartInputChange(index, e)}
                                                placeholder="Qty"
                                                className="w-[100px]"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleRemoveSparePartRow(index)}
                                                disabled={newSpareParts.length === 1}
                                            >
                                                <Trash2 className="h-4 w-4 text-red-500" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        
                        <div className="mt-4 flex justify-end">
                            <Button onClick={handleAddSparePartRow} variant="outline" size="sm" className="flex items-center gap-2">
                                <Plus className="h-4 w-4" /> Add Row
                            </Button>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="submit" onClick={() => setIsAddSparePartConfirmOpen(true)}>Add to Inventory</Button>
                    </DialogFooter>
                    <AlertDialog open={isAddSparePartConfirmOpen} onOpenChange={setIsAddSparePartConfirmOpen}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Add to Inventory?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    Are you sure you want to add these items to inventory?
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={handleSaveSparePart}>Confirm</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </DialogContent>
            </Dialog>

            {/* Edit Spare Part Modal */}
            <Dialog open={isEditSparePartOpen} onOpenChange={setIsEditSparePartOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Edit Spare Part</DialogTitle>
                        <DialogDescription>
                            Update the details of the spare part stock.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedSparePart && (
                        <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-brand" className="text-right">
                                    Brand
                                </Label>
                                <div className="col-span-3">
                                    <Select 
                                        value={selectedSparePart.brand} 
                                        onValueChange={(value) => setSelectedSparePart(prev => ({ ...prev, brand: value }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Brand" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {brands.map(brand => (
                                                <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-itemDescription" className="text-right">
                                    Description
                                </Label>
                                <Input
                                    id="edit-itemDescription"
                                    name="itemDescription"
                                    value={selectedSparePart.itemDescription}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-itemCode" className="text-right">
                                    Part Number
                                </Label>
                                <Input
                                    id="edit-itemCode"
                                    name="itemCode"
                                    value={selectedSparePart.itemCode}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-quantity" className="text-right">
                                    Quantity
                                </Label>
                                <Input
                                    id="edit-quantity"
                                    name="quantity"
                                    type="number"
                                    value={selectedSparePart.quantity}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="submit" onClick={handleUpdateSparePart}>Save Changes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Brand Modal */}
            <Dialog open={isAddBrandOpen} onOpenChange={setIsAddBrandOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Add New Brand</DialogTitle>
                        <DialogDescription>
                            Enter a new brand name to add to the list.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="brand-name" className="text-right">
                                Name
                            </Label>
                            <Input
                                id="brand-name"
                                value={newBrandName}
                                onChange={(e) => setNewBrandName(e.target.value)}
                                className="col-span-3"
                                placeholder="Enter brand name..."
                            />
                        </div>
                        
                        <div className="mt-4 border rounded-md p-2 max-h-[200px] overflow-y-auto">
                            <Label className="mb-2 block text-sm font-medium">Brands</Label>
                            {brands.length === 0 ? (
                                <p className="text-sm text-gray-500">No brands found.</p>
                            ) : (
                                <ul className="text-sm space-y-1">
                                    {brands.map((brand) => (
                                        <li key={brand.id} className="flex justify-between items-center text-gray-700 p-1 hover:bg-gray-100 rounded">
                                            <span>• {brand.name}</span>
                                            <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                onClick={() => handleDeleteBrand(brand.id)}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="submit" onClick={handleSaveBrand}>Save Brand</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SpareParts;
